import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiTokenService } from '../service/api-token.service';

@Injectable()
export class ApiTokenInterceptor implements HttpInterceptor {
  private readonly allowedHosts = this.buildAllowedHosts();

  constructor(private readonly apiTokenService: ApiTokenService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.headers.has('Authorization')) {
      return next.handle(req);
    }

    if (!this.shouldAttachToken(req.url)) {
      return next.handle(req);
    }

    const token = this.apiTokenService.getToken();
    if (!token) {
      return next.handle(req);
    }

    return next.handle(
      req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
  }

  private shouldAttachToken(url: string): boolean {
    if (!/^https?:\/\//i.test(url)) {
      return true;
    }

    try {
      return this.allowedHosts.has(new URL(url).host);
    } catch {
      return false;
    }
  }

  private buildAllowedHosts(): Set<string> {
    const urls = [
      ...this.extractHttpUrls(environment.cloudrun),
      ...this.extractHttpUrls(environment.gke),
    ];

    const hosts = urls
      .map((url) => {
        try {
          return new URL(url).host;
        } catch {
          return '';
        }
      })
      .filter((host): host is string => !!host);

    return new Set(hosts);
  }

  private extractHttpUrls(value: unknown): string[] {
    if (typeof value === 'string') {
      return /^https?:\/\//i.test(value) ? [value] : [];
    }

    if (Array.isArray(value)) {
      return value.flatMap((item) => this.extractHttpUrls(item));
    }

    if (value && typeof value === 'object') {
      return Object.values(value as Record<string, unknown>).flatMap((item) => this.extractHttpUrls(item));
    }

    return [];
  }
}
